import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useApi } from "@lending/lib/api";
import { getMembershipShareEndpoint } from "@lending/pages/Common/MembershipShare/hooks/getMembershipShareEndpoint";
import type { MembershipRequirementResponse, MembershipSelectionPayload } from "@lending/types";

interface UseSelectMembershipQueryProps {
  onSuccess: (response: MembershipRequirementResponse) => Promise<void> | void;
}

function useSelectMembershipQuery({ onSuccess }: UseSelectMembershipQueryProps) {
  const $api = useApi();
  const queryClient = useQueryClient();
  const lendingApplicationUUID = useLendingApplicationID();
  const productType = useProductType();
  const { showGenericErrorToast } = useToastsContext();
  const endpoint = getMembershipShareEndpoint(productType, "membership_selection");
  const membershipShareAccessEndpoint = getMembershipShareEndpoint(
    productType,
    "membership_share_access"
  );

  const applicationQueryKeyByProductType = {
    "personal-loan": ["get", "/v2/lending/personal_loan_applications/{uuid}/"],
    "credit-card": ["get", "/v2/lending/credit_card_applications/{uuid}/"],
    "auto-loan": ["get", "/v2/lending/vehicle_loan_applications/{uuid}/"],
  } as const;

  const mutation = $api.useMutation("post", endpoint, {
    onSuccess: async (data: MembershipRequirementResponse) => {
      // Selecting an existing membership submits it, so refresh the cached state
      // before navigating; otherwise the route policy still sees MEMBERSHIP_SELECTION
      // and bounces straight back to this page. Create-new stays in the membership
      // flow, where refreshing this early would redirect to activate before the
      // account application exists.
      if (productType && data.state !== "membership_share_pending") {
        await queryClient.invalidateQueries({
          queryKey: applicationQueryKeyByProductType[productType],
        });
        // Stale, but never refetched: the endpoint rejects submitted applications and
        // the route resolver treats an access-query error as fatal.
        await queryClient.invalidateQueries({
          queryKey: ["get", membershipShareAccessEndpoint],
          refetchType: "none",
        });
      }

      await onSuccess({
        state: data.state,
        membership_share_verification_required: data.membership_share_verification_required,
        membership_share_email_conflict: data.membership_share_email_conflict,
      });
    },
    onError: (error) => {
      showGenericErrorToast();
      Sentry.captureException(error, {
        extra: { lendingApplicationUUID, productType },
      });
    },
  });

  const selectMembership = async (payload: MembershipSelectionPayload) => {
    if (!lendingApplicationUUID) {
      throw new Error("Membership selection endpoint is not available");
    }

    return mutation.mutateAsync({
      params: {
        path: {
          uuid: lendingApplicationUUID,
        },
      },
      body: payload,
    });
  };

  return {
    selectMembership,
    isPending: mutation.isPending,
  };
}

export { useSelectMembershipQuery };
